import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import IcomoonReact from 'icomoon-react';
import Select from 'react-select';

import FolderAccessConfirmation from './FolderAccessConfirmation';
import { CustomMultiValue, CustomOption, customSelectStyles } from './DocumentSelectComponents';
import {
  isUserInSelectedGroups,
  getVisibleUsers,
  getSelectedUserIds,
  getInitialGroupIds,
  handleUserChange,
  handleGroupChange,
  defaultFolderFunction,
  canEditGroupAccess,
  foldersRefiner,
  groupsRefiner,
  usersRefiner,
  getSelectionIds,
  getIdChanges,
} from '../utils/documentAccessHelpers';
import useDocuments from '../hooks/useDocuments';
import NoHoverButton from '../../../../shared/components/NoHoverButton';
import Text from '../../../../shared/components/Text/Text';
import SubText from '../../../../shared/components/SubText/SubText';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Button from '../../../../shared/components/Button/Button';
import { showInfoToast } from '../../../../shared/utils/toastManager';

const ModalWrapper = styled('div')`
  display: flex;
  width: 100%;
  margin-bottom: 27px;
`;

const RightWrapper = styled('div')`
  width: 50%;
  display: flex;
  justify-content: right;
  align-items: center;
  .formik-form {
    width: 100%;
  }
`;

const TitleError = styled('div')`
  font-size: 14px;
  color: #ff6600;
  width: 100%;
  display: flex;
  justify-content: center;
`;

const FieldError = styled('div')`
  font-size: 14px;
  color: #ff6600;
  margin-top: 12px;
`;

const NavigationWrapper = styled('div')`
  display: flex;
  justify-content: flex-end;
  margin-top: -20px;
  width: 100%;
  z-index: 1;
  .back-arrow-div {
    cursor: pointer;
  }
`;

const FormWrapper = styled('div')`
  display: flex;
  justify-content: left;
  flex-direction: row;
  width: 100%;
  height: 100%;
`;

const AnotherWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
`;

const LeftWrapper = styled('div')`
  width: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  align-items: right;
  height: 100%;

  .icon-text {
    margin-top: 10px;
    font-size: 16px;
  }
`;

const IconTextWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  .update-button {
    width: 170px;
    margin-top: 15px;
    color: black;
    background: white;
    border: 1px solid black;
    &:hover {
      border: 1px solid black;
      opacity: .7;
    }
  }
`;

const URLWrapper = styled('div')`
  width: 90%;
  margin-bottom: 20px;
`;

const TitlePlusErrorWrapper = styled('div')`
  display: flex;
  justify-content: space-between;
`;

const FormGroup = styled('div')`
  p {
    margin-top: 12px;
  }
  label {
    width: 100%;
  }
  button {
    margin-bottom: 20px;
  }
  .easy-join {
    margin-bottom: 15px;
  }

  z-index: 1;
  @media screen and (max-width: 600px) {
    a {
      font-size: 11px;
    }
  }
  .form-control {
    min-height: 30px;
    width: 100%;
  }
  .folder-text {
    font-weight: bold;
  }
  .group-access-text {
    font-weight: bold;
    margin-top: 12px;
  }
`;

const ModalUpdateDocument = ({
  handleUpdateDocument,
  currentUser,
  document,
  onRequestClose,
  currentFolder,
}) => {
  const {
    handleFetchFile,
    handleFetchUserGroups,
    handleFetchDocuments,
    handleFetchFolders,
  } = useDocuments();

  const updateDocumentError = useSelector((state) => state.documents.data.updateDocumentError);
  const visibleFolders = useSelector((state) => state.documents.data.folders);
  const userGroups = useSelector((state) => state.documents.data.userGroups);
  const userList = useSelector((state) => state.documents.data.userList);
  const folders = useSelector((state) => state.documents.data.folders);
  const documents = useSelector((state) => state.documents.data.documents);
  const folderDocuments = useSelector((state) => state.documents.data.folderDocuments);
  const isDocumentUpdating = useSelector((state) => state.documents.ui.isDocumentUpdating);

  const [selectedFolder, setSelectedFolder] = useState({});
  const [selectedGroups, setSelectedGroups] = useState(
    () => getInitialGroupIds(document),
  );
  const [fadingUsers, setFadingUsers] = useState([]);
  const [hasShownAutoRemovalToast, setHasShownAutoRemovalToast] = useState(false);
  const [showAccessConfirmation, setShowAccessConfirmation] = useState(false);
  const [pendingSubmitValues, setPendingSubmitValues] = useState(null);
  const [newlyAddedGroups, setNewlyAddedGroups] = useState([]);
  const [newlyAddedUsers, setNewlyAddedUsers] = useState([]);
  const [isRemovalChange, setIsRemovalChange] = useState(false);
  const [willBecomeGlobal, setWillBecomeGlobal] = useState(false);

  // Fetches list of potential folders
  useEffect(() => {
    const accountCode = currentUser !== undefined && currentUser.accountCode;
    if (accountCode !== undefined && !userGroups) {
      handleFetchUserGroups(accountCode);
    }
    if (!currentFolder && !visibleFolders.length) {
      handleFetchFolders(accountCode);
    }
  }, []);

  const fileFetcherDownloader = async (accountCode, docId) => {
    await handleFetchFile({ accountCode, docId });
  };

  const iconResolver = () => {
    if (document.type === 0) {
      return 'folder';
    }
    if (document.type === 1) {
      return 'file';
    }
    if (document.type === 2) {
      return 'globe';
    }
    return null;
  };

  const updateText = () => {
    if (document.type === 0) {
      return 'FOLDER';
    }
    if (document.type === 1) {
      return 'FILE';
    }
    if (document.type === 2) {
      return 'LINK';
    }
    return null;
  };

  // Close confirmation modal and reset state
  const resetAccessConfirmation = () => {
    setShowAccessConfirmation(false);
    setPendingSubmitValues(null);
    setNewlyAddedGroups([]);
    setNewlyAddedUsers([]);
    setIsRemovalChange(false);
    setWillBecomeGlobal(false);
  };

  const submitDocumentUpdate = async (values, cascadePermissions) => {
    const newValues = { ...values };

    if (document.type === 0) {
      delete newValues.folderId;
    }

    // Normalise form selections into ID arrays
    let groupIds = Array.isArray(newValues.groups)
      ? newValues.groups.map((group) => (typeof group === 'object' ? group.id : group))
      : [];

    let userIds = Array.isArray(newValues.users)
      ? newValues.users.map((user) => (typeof user === 'object' ? user.id : user))
      : [];

    const originalGroupIds = (document.userGroupAccess || [])
      .filter((group) => group.name !== null)
      .map((group) => group.id);

    const originalUserIds = (document.individualAccess || [])
      .filter((user) => user.first !== null && user.last !== null)
      .map((user) => user.id);

    const hadExistingAccess = originalGroupIds.length > 0 || originalUserIds.length > 0;
    const accessFieldsEmpty = groupIds.length === 0 && userIds.length === 0;

    const folderContext = (selectedFolder && selectedFolder.id && selectedFolder.id !== 0)
      ? selectedFolder
      : currentFolder;

    // If fields are blank and doc had previous access then inherit from folder
    if (folderContext && accessFieldsEmpty && hadExistingAccess) {
      groupIds = folderContext.userGroupAccess?.map((group) => group.id) || [];

      const folderIndividualIds = (folderContext.individualAccess || [])
        .filter((user) => user.first !== null && user.last !== null)
        .map((user) => user.id);

      userIds = folderIndividualIds;
    }

    newValues.groups = groupIds;
    newValues.users = userIds;

    if (!newValues.linkUrl) {
      delete newValues.linkUrl;
    }

    const indexOb = {};
    if (currentFolder) {
      indexOb.folderIndex = folderDocuments.findIndex(
        (documentItem) => documentItem.id === document.id,
      );
      if (document.parentFolderId !== values.folderId) {
        indexOb.oldFolderIndex = indexOb.folderIndex;
      }
    } else if (document.type === 0) {
      indexOb.folderDocIndex = folders.findIndex(
        (documentItem) => documentItem.id === document.id,
      );
      indexOb.documentIndex = documents.findIndex(
        (documentItem) => documentItem.id === document.id,
      );
    } else {
      indexOb.documentIndex = documents.findIndex(
        (documentItem) => documentItem.id === document.id,
      );
      if (document.parentFolderId !== values.folderId) {
        indexOb.oldDocumentIndex = indexOb.documentIndex;
      }
    }

    const finalCascadeValue = cascadePermissions && document.type === 0;

    await handleUpdateDocument(
      currentUser.accountCode,
      document.id,
      newValues,
      indexOb,
      finalCascadeValue,
    );

    if (finalCascadeValue) {
      handleFetchDocuments(currentUser.accountCode);
    }

    resetAccessConfirmation();
  };

  // Opens confirmation modal and sets state
  const openAccessConfirmation = ({
    values,
    groupsForSummary,
    usersForSummary,
    isRemovalOnly,
    willBecomeGlobal: nextWillBecomeGlobal,
  }) => {
    setNewlyAddedGroups(groupsForSummary);
    setNewlyAddedUsers(usersForSummary);
    setPendingSubmitValues(values);
    setIsRemovalChange(isRemovalOnly);
    setWillBecomeGlobal(nextWillBecomeGlobal);
    setShowAccessConfirmation(true);
  };

  const handleSubmit = (values) => {
  // Only folders need the cascade confirmation logic
    if (document.type !== 0) {
      submitDocumentUpdate(values, false);
      return;
    }

    const originalGroupIds = (document.userGroupAccess || [])
      .filter((group) => group.name !== null)
      .map((group) => group.id);

    const originalUserIds = (document.individualAccess || [])
      .filter((user) => user.first !== null && user.last !== null)
      .map((user) => user.id);

    const newGroupIds = getSelectionIds(values.groups);
    const newUserIds = getSelectionIds(values.users);

    const { addedIds: addedGroupIds, removedIds: removedGroupIds } = getIdChanges(
      originalGroupIds,
      newGroupIds,
    );
    const { addedIds: addedUserIds, removedIds: removedUserIds } = getIdChanges(
      originalUserIds,
      newUserIds,
    );

    const hasAddedAccess = addedGroupIds.length > 0 || addedUserIds.length > 0;
    const hasRemovedAccess = removedGroupIds.length > 0 || removedUserIds.length > 0;
    const hasAccessChange = hasAddedAccess || hasRemovedAccess;

    if (!hasAccessChange) {
    // No permission change then just submit normally
      submitDocumentUpdate(values, false);
      return;
    }

    const isRemovalOnly = hasRemovedAccess && !hasAddedAccess;

    const shouldBecomeGlobal = (
      isRemovalOnly
    && newGroupIds.length === 0
    && newUserIds.length === 0
    && (originalGroupIds.length > 0 || originalUserIds.length > 0)
    );

    const addedGroups = Array.isArray(values.groups)
      ? values.groups.filter((group) => {
        const id = typeof group === 'object' ? group.id : group;
        return addedGroupIds.includes(id);
      })
      : [];

    const addedUsers = Array.isArray(values.users)
      ? values.users.filter((user) => {
        const id = typeof user === 'object' ? user.id : user;
        return addedUserIds.includes(id);
      })
      : [];

    const removedGroups = removedGroupIds.map((id) => ({ id }));
    const removedUsers = removedUserIds.map((id) => ({ id }));

    const groupsForSummary = isRemovalOnly ? removedGroups : addedGroups;
    const usersForSummary = isRemovalOnly ? removedUsers : addedUsers;

    openAccessConfirmation({
      values,
      groupsForSummary,
      usersForSummary,
      isRemovalOnly,
      willBecomeGlobal: shouldBecomeGlobal,
    });
  };

  const displayGroups = groupsRefiner({ userGroups, currentFolder, selectedFolder });
  const displayUsers = usersRefiner({
    userList,
    currentFolder,
    selectedFolder,
    allowMainDirectoryBypass: true,
    currentUser,
  });

  const emojiRegex = /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F700}-\u{1F77F}\u{1F780}-\u{1F7FF}\u{1F800}-\u{1F8FF}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA6F}\u{1FA70}-\u{1FAFF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F1E0}-\u{1F1FF}]/gu;

  const validationSchema = Yup.object().shape({
    linkUrl: document.type === 2
      ? Yup.string().url('Invalid URL').required('URL is required')
      : Yup.string().nullable(),
    name: Yup.string()
      .required('Name is required')
      .test('no-emojis', 'Emojis are not allowed', (value) => !value || !emojiRegex.test(value)),
    description: Yup.string()
      .test('no-emojis', 'Emojis are not allowed', (value) => !value || !emojiRegex.test(value)),
  });

  return (
    <>
      {!showAccessConfirmation && (
        <ModalWrapper>
          <Formik
            initialValues={{
              name: document.name,
              description: document.description,
              groups: document.userGroupAccess.filter((group) => group.name !== null),
              users: document.individualAccess
                ? document.individualAccess.filter(
                  (user) => user.first !== null && user.last !== null,
                )
                : [],
              folderId: document.parentFolderId ?? currentFolder?.id ?? 0,
              linkUrl: document.url,
            }}
            validationSchema={validationSchema}
            validateOnChange
            onSubmit={(values, { setSubmitting }) => {
              setTimeout(() => {
                handleSubmit(values);
                setSubmitting(false);
              });
            }}
            render={({
              submitForm,
              setFieldValue,
              values,
              errors,
              touched,
            }) => {
              const selectedUserIds = getSelectedUserIds(values.users);
              const folderOptions = currentFolder
                ? [currentFolder, { id: 0, name: 'Main Directory', type: 0 }]
                : foldersRefiner({
                  visibleFolders,
                  userList,
                  selectedGroupIds: selectedGroups,
                  selectedUserIds,
                });
              const defaultFolder = defaultFolderFunction(values.folderId, folderOptions);
              return (
                <ModalWrapper>
                  <AnotherWrapper>
                    <NavigationWrapper>
                      <Text
                        className='back-arrow-div'
                        onClick={() => {
                          onRequestClose();
                        }}
                      >
                        <IcomoonReact
                          iconSet={iconSet}
                          size={10}
                          icon='remove'
                        />
                      </Text>
                    </NavigationWrapper>
                    <FormWrapper>
                      <LeftWrapper>
                        <IconTextWrapper>
                          <IcomoonReact
                            iconSet={iconSet}
                            size={75}
                            icon={iconResolver()}
                          />
                          <SubText className='icon-text'>
                            UPDATE
                            {' '}
                            {updateText()}
                          </SubText>
                          {document.type === 1 ? (
                            <Button
                              className='update-button'
                              cta='Preview File'
                              noHover
                              noBorder
                              primary
                              type='button'
                              onClick={() => {
                                const { accountCode } = currentUser;
                                const docId = document.id;
                                fileFetcherDownloader(accountCode, docId, document.name);
                              }}
                            />
                          ) : null}
                        </IconTextWrapper>
                        {document.type === 2 ? (
                          <URLWrapper>
                            <FormGroup>
                              <TitlePlusErrorWrapper>
                                <Text><strong>URL</strong></Text>
                                {errors.linkUrl && touched.linkUrl ? (
                                  <FieldError className='error-text'>{errors.linkUrl}</FieldError>
                                ) : null}
                              </TitlePlusErrorWrapper>
                              <Field
                                className='form-control'
                                id='url-field'
                                name='linkUrl'
                                placeholder='https://domain.com'
                              />
                            </FormGroup>
                            <FormGroup>
                              <Text className='group-access-text'>Individual Access</Text>
                              <Select
                                key={selectedGroups.join('-') || 'no-groups'}
                                defaultValue={values.users}
                                getOptionLabel={(option) => `${option.first} ${option.last}`}
                                getOptionValue={(option) => option.id}
                                options={displayUsers.filter(
                                  (user) => user.first !== null && user.last !== null,
                                )}
                                value={getVisibleUsers(displayUsers, selectedUserIds, fadingUsers)}
                                selectedGroups={selectedGroups}
                                onChange={(options) => handleUserChange(options, setFieldValue)}
                                components={{
                                  Option: CustomOption,
                                  MultiValue: CustomMultiValue,
                                }}
                                fadingUsers={fadingUsers}
                                isUserInSelectedGroups={isUserInSelectedGroups}
                                styles={customSelectStyles}
                                isOptionDisabled={(
                                  option,
                                ) => isUserInSelectedGroups(option, selectedGroups)}
                                isClearable
                                isMulti
                              />
                            </FormGroup>
                          </URLWrapper>
                        ) : null}
                      </LeftWrapper>
                      <RightWrapper>
                        <Form className='formik-form'>
                          <FormGroup>
                            <TitlePlusErrorWrapper>
                              <Text><strong>Name</strong></Text>
                              {errors.name && touched.name ? (
                                <FieldError className='error-text'>{errors.name}</FieldError>
                              ) : null}
                            </TitlePlusErrorWrapper>
                            <Field
                              className='form-control'
                              id='name-field'
                              name='name'
                              placeholder='Document Name'
                            />
                          </FormGroup>
                          <FormGroup>
                            <TitlePlusErrorWrapper>
                              <Text><strong>Description</strong></Text>
                              {errors.description && touched.description ? (
                                <FieldError className='error-text'>{errors.description}</FieldError>
                              ) : null}
                            </TitlePlusErrorWrapper>
                            <Field
                              className='form-control'
                              id='description-field'
                              name='description'
                              placeholder='Document Description'
                            />
                          </FormGroup>
                          {document.type !== 0 ? (
                            <FormGroup>
                              <Text className='folder-text'>Folder</Text>
                              <Select
                                defaultValue={defaultFolder}
                                getOptionLabel={(option) => option.name}
                                getOptionValue={(option) => option.id}
                                options={folderOptions}
                                onChange={(folder) => {
                                  setSelectedFolder(folder);
                                  if (folder && folder.id) {
                                    setFieldValue('folderId', folder.id);
                                  } else {
                                    setFieldValue('folderId', 0);
                                  }
                                }}
                                isClearable
                              />
                            </FormGroup>
                          ) : null}
                          <FormGroup>
                            <Text className='group-access-text'>Group Access</Text>
                            <Select
                              value={values.groups}
                              getOptionLabel={(option) => option.name}
                              getOptionValue={(option) => option.id}
                              options={displayGroups.filter((group) => group.name !== null)}
                              onChange={(options) => handleGroupChange({
                                options,
                                values,
                                setFieldValue,
                                userList,
                                isUserInSelectedGroups,
                                setFadingUsers,
                                hasShownAutoRemovalToast,
                                setHasShownAutoRemovalToast,
                                showInfoToast,
                                selectedGroupIds: selectedGroups,
                                setSelectedGroups,
                                storeGroupsAsObjects: true,
                              })}
                              isDisabled={!canEditGroupAccess(document, userGroups)}
                              styles={customSelectStyles}
                              isClearable
                              isMulti
                            />
                          </FormGroup>
                          {document.type !== 2 ? (
                            <FormGroup>
                              <Text className='group-access-text'>Individual Access</Text>
                              <Select
                                key={selectedGroups.join('-') || 'no-groups'}
                                defaultValue={values.users}
                                getOptionLabel={(option) => `${option.first} ${option.last}`}
                                getOptionValue={(option) => option.id}
                                options={displayUsers.filter(
                                  (user) => user.first !== null && user.last !== null,
                                )}
                                value={getVisibleUsers(displayUsers, selectedUserIds, fadingUsers)}
                                selectedGroups={selectedGroups}
                                onChange={(options) => handleUserChange(options, setFieldValue)}
                                components={{
                                  Option: CustomOption,
                                  MultiValue: CustomMultiValue,
                                }}
                                fadingUsers={fadingUsers}
                                isUserInSelectedGroups={isUserInSelectedGroups}
                                styles={customSelectStyles}
                                isOptionDisabled={(
                                  option,
                                ) => isUserInSelectedGroups(option, selectedGroups)}
                                isClearable
                                isMulti
                              />
                            </FormGroup>
                          ) : null}
                        </Form>
                      </RightWrapper>
                    </FormWrapper>
                    {updateDocumentError ? <TitleError className='error-text'>{updateDocumentError}</TitleError> : null}
                  </AnotherWrapper>
                  <NoHoverButton
                    bottom
                    fullWidth
                    cta={!isDocumentUpdating ? `Update ${updateText()}` : 'Updating...'}
                    customColor='#10cd8c'
                    className='modal-button'
                    disabled={isDocumentUpdating}
                    large
                    noBorder
                    primary
                    square
                    onClick={() => {
                      submitForm();
                    }}
                  />
                </ModalWrapper>
              );
            }}
          />
        </ModalWrapper>
      )}
      {showAccessConfirmation && (
        <FolderAccessConfirmation
          newGroups={newlyAddedGroups}
          newUsers={newlyAddedUsers}
          isRemovalChange={isRemovalChange}
          willBecomeGlobal={willBecomeGlobal}
          onApplyToAll={() => {
            submitDocumentUpdate(pendingSubmitValues, true);
          }}
          onCancel={() => {
            submitDocumentUpdate(pendingSubmitValues, false);
          }}
        />
      )}
    </>
  );
};

ModalUpdateDocument.propTypes = {
  handleUpdateDocument: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  document: PropTypes.instanceOf(Object).isRequired,
  currentUser: PropTypes.instanceOf(Object).isRequired,
  currentFolder: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.bool,
  ]),
};

ModalUpdateDocument.defaultProps = {
  currentFolder: null,
};

export default ModalUpdateDocument;
